// When the cart lapses, resolved the same way everywhere it is asked.
//
// The cart used to be timed by three clocks that disagreed: the visible
// countdown ran off `lastAddedBookToCart` + a CART_TIMEOUT constant baked into
// settings.json, `utils/ExpiredCart.js` purged localStorage off each item's own
// `expired` timestamp, and the server abandoned the booking on an in-process
// timer of its own. A customer could watch 40 minutes tick down on a cart that a
// page reload had already emptied.
//
// SiteSetting.carttimeout (Admin -> Settings) is now the single source of truth.
// The booking stores only the anchor -- `cartHoldStartedAt`, pushed forward for
// the whole cart on every add -- and both this countdown and the server's sweeper
// derive the deadline from that anchor plus the current setting. Change the
// setting and every open cart moves with it, no stored deadline to migrate.

// Only used when a cart item predates the `expired` field, or the value on it is
// unreadable. Mirrors DEFAULT_CART_TIMEOUT_MINUTES on the server.
export const DEFAULT_CART_TIMEOUT_MINUTES = 60;

/** Parse a stored date defensively -- returns null rather than an Invalid Date. */
const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * The cart timeout an admin configured, in minutes.
 * @param {object} setting - the public site-settings object (state.setting.setting)
 */
export const getCartTimeoutMinutes = (setting) => {
  const minutes = Number(setting?.carttimeout);
  if (!Number.isFinite(minutes) || minutes <= 0) return DEFAULT_CART_TIMEOUT_MINUTES;
  return Math.round(minutes);
};

/**
 * The configured timeout for code that runs before redux exists.
 *
 * `deleteExpiredCart` runs at module scope in App.js, so it cannot read the
 * store; it reads the same `jsonstring` cache the settings reducer hydrates
 * from. Falls back to the default when nothing is cached yet.
 */
export const getCachedCartTimeoutMinutes = () => {
  try {
    return getCartTimeoutMinutes(JSON.parse(localStorage.getItem("jsonstring")));
  } catch (error) {
    return DEFAULT_CART_TIMEOUT_MINUTES;
  }
};

/**
 * When the cart lapses.
 *
 * Computed the same way the server's sweeper computes it: the hold's anchor
 * (`cartHoldStartedAt`) plus the timeout as configured *right now*. Deriving it
 * rather than reading the stored `expired` deadline is what makes an admin's
 * change to carttimeout show up on a cart that is already open -- drop the
 * setting to 10 minutes and a cart anchored 4 minutes ago has 6 minutes left,
 * not the 60 it was created with.
 *
 * Falls back through the stored deadline (rows written before the anchor
 * existed) and then the legacy localStorage marker.
 *
 * Takes the earliest across the items: that is what actually decides the cart's
 * fate, since ExpiredCart purges and the sweeper flags on the first to lapse.
 * The server keeps them in step, so in practice they are the same instant.
 *
 * @param {Array}  cart - cart items as stored in localStorage / redux
 * @param {string} lastAddedBookToCart - legacy fallback timestamp
 * @param {number} timeoutMinutes - the configured hold, from getCartTimeoutMinutes()
 * @returns {Date|null} null when the cart is empty
 */
export const getCartExpiresAt = (
  cart,
  lastAddedBookToCart,
  timeoutMinutes = DEFAULT_CART_TIMEOUT_MINUTES
) => {
  if (!Array.isArray(cart) || cart.length === 0) return null;

  const windowMs = timeoutMinutes * 60 * 1000;

  const anchors = cart.map((item) => toDate(item?.cartHoldStartedAt)).filter(Boolean);
  if (anchors.length > 0) {
    return new Date(Math.min(...anchors.map((date) => date.getTime())) + windowMs);
  }

  // Written before the anchor existed: the stored deadline is all there is.
  const deadlines = cart.map((item) => toDate(item?.expired)).filter(Boolean);
  if (deadlines.length > 0) {
    return new Date(Math.min(...deadlines.map((date) => date.getTime())));
  }

  // Carts sitting in a browser's localStorage from before either field. Time
  // those off the old marker so the countdown does not jump, and let the next
  // add-to-cart bring them onto the server clock.
  const lastAdded = toDate(lastAddedBookToCart);
  const origin = lastAdded ? lastAdded.getTime() : Date.now();
  return new Date(origin + windowMs);
};

/**
 * Restart the hold across the whole cart, mirroring what the server just did.
 *
 * Creating a booking pushes `cartHoldStartedAt` forward on every row sharing the
 * `cartID`, but only the new row comes back in the response -- the items already
 * in localStorage still carry their old anchor. Since the countdown takes the
 * *earliest* anchor in the cart, that stale copy would keep the timer ticking
 * down from the first item's deadline and the reset would never be visible.
 *
 * @param {Array}  cart   - cart items to restamp
 * @param {object} source - the newly created booking, as returned by the server
 * @returns {Array} a new array; the input is left alone
 */
export const applyCartHold = (cart, source) => {
  if (!Array.isArray(cart)) return cart;

  const anchor = source?.cartHoldStartedAt;
  const expired = source?.expired;

  // Nothing to copy (a server that predates the anchor): leave the cart as it is
  // and let getCartExpiresAt fall back to each item's stored deadline.
  if (!anchor && !expired) return cart;

  return cart.map((item) => ({
    ...item,
    ...(anchor ? { cartHoldStartedAt: anchor } : {}),
    ...(expired ? { expired } : {}),
  }));
};

/** Whether the cart's hold has already lapsed. */
export const isCartExpired = (cart, lastAddedBookToCart, timeoutMinutes) => {
  const expiresAt = getCartExpiresAt(cart, lastAddedBookToCart, timeoutMinutes);
  return expiresAt !== null && expiresAt.getTime() <= Date.now();
};
